import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Search, 
  User, 
  Headphones, 
  Clock, 
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  Bot,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { SecuritySanitizer } from '@/utils/security/sanitizer';

interface EnhancedTranscriptProps {
  transcript: string;
  duration?: number;
}

interface TranscriptMessage {
  id: number;
  speaker: 'interviewer' | 'candidate' | 'screening-agent';
  content: string;
  timestamp?: string;
  isQuestion?: boolean;
  highlightedContent?: string;
}

export const EnhancedTranscript: React.FC<EnhancedTranscriptProps> = ({ 
  transcript, 
  duration 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [highlightedMessages, setHighlightedMessages] = useState<{ [key: number]: string }>({});

  // AI Highlighting for candidate responses
  const handleHighlightCandidateResponses = async () => {
    setIsHighlighting(true);
    try {
      const candidateResponses = messages
        .filter(m => m.speaker === 'candidate')
        .map(m => ({ id: m.id, content: m.content }));

      if (candidateResponses.length === 0) {
        toast.error('No candidate responses found to highlight');
        return;
      }

      const { data, error } = await supabase.functions.invoke('highlight-candidate-responses', {
        body: { candidateResponses }
      });

      if (error) throw error;

      if (data?.highlightedResponses) {
        const highlightMap: { [key: number]: string } = {};
        data.highlightedResponses.forEach((item: any) => {
          highlightMap[item.id] = item.highlightedContent;
        });
        setHighlightedMessages(highlightMap);
        toast.success('AI highlighting applied to candidate responses');
      }
    } catch (error) {
      console.error('Error highlighting responses:', error);
      toast.error('Failed to highlight candidate responses');
    } finally {
      setIsHighlighting(false);
    }
  };

  // Parse transcript into structured messages
  const parseTranscript = (text: string): TranscriptMessage[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const messages: TranscriptMessage[] = [];
    
    lines.forEach((line, index) => {
      // Handle different speaker formats
      if (line.match(/^(agent|interviewer):\s*/i)) {
        const content = line.replace(/^(agent|interviewer):\s*/i, '').trim();
        messages.push({
          id: index,
          speaker: 'interviewer',
          content,
          isQuestion: content.includes('?')
        });
      } else if (line.match(/^(user|candidate):\s*/i)) {
        const content = line.replace(/^(user|candidate):\s*/i, '').trim();
        messages.push({
          id: index,
          speaker: 'candidate',
          content
        });
      } else if (line.match(/^screening[\s-]?agent:\s*/i)) {
        const content = line.replace(/^screening[\s-]?agent:\s*/i, '').trim();
        messages.push({
          id: index,
          speaker: 'interviewer',
          content,
          isQuestion: content.includes('?')
        });
      } else if (line.trim()) {
        // For lines without clear speaker prefix, try to infer from context
        // If it's a continuation or unclear, treat as screening agent
        messages.push({
          id: index,
          speaker: 'interviewer',
          content: line.trim(),
          isQuestion: line.trim().includes('?')
        });
      }
    });

    return messages;
  };

  const messages = parseTranscript(transcript);

  // Filter messages based on search term
  const filteredMessages = messages.filter(message =>
    message.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Statistics
  const stats = {
    totalMessages: messages.length,
    interviewerMessages: messages.filter(m => m.speaker === 'interviewer').length,
    candidateMessages: messages.filter(m => m.speaker === 'candidate').length,
    questions: messages.filter(m => m.isQuestion).length
  };

  const handleCopyMessage = async (content: string, id: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      toast.success('Message copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy message');
    }
  };

  const handleCopyFullTranscript = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      toast.success('Full transcript copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy transcript');
    }
  };

  if (!transcript?.trim()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Transcript
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No transcript available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Enhanced Transcript
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleHighlightCandidateResponses}
              disabled={isHighlighting}
              className="flex items-center gap-2"
            >
              <Sparkles className="h-4 w-4" />
              {isHighlighting ? 'Highlighting...' : 'AI Highlight'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyFullTranscript}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              Copy All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            {stats.totalMessages} messages
          </div>
          <div className="flex items-center gap-1">
            <Headphones className="h-4 w-4" />
            {stats.interviewerMessages} interviewer
          </div>
          <div className="flex items-center gap-1">
            <User className="h-4 w-4" />
            {stats.candidateMessages} candidate
          </div>
          {stats.questions > 0 && (
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className="text-xs">
                {stats.questions} questions
              </Badge>
            </div>
          )}
          {duration && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {Math.round(duration / 60)}m {duration % 60}s
            </div>
          )}
        </div>

        {/* Search */}
        {isExpanded && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search transcript..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <ScrollArea className="h-[500px] w-full">
            <div className="space-y-4">
              {filteredMessages.map((message, index) => (
                <div key={message.id} className="group">
                  <div
                    className={`flex items-start gap-3 p-4 rounded-lg border transition-all hover:shadow-sm ${
                      message.speaker === 'interviewer'
                        ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-950/20 dark:border-blue-800/30'
                        : message.speaker === 'candidate'
                        ? 'bg-green-50/50 border-green-100 dark:bg-green-950/20 dark:border-green-800/30'
                        : 'bg-muted/30 border-muted'
                    }`}
                  >
                    {/* Speaker Avatar */}
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        message.speaker === 'interviewer'
                          ? 'bg-blue-500'
                          : message.speaker === 'candidate'
                          ? 'bg-green-500'
                          : 'bg-muted-foreground'
                      }`}
                    >
                      {message.speaker === 'interviewer' ? (
                        <Headphones className="h-4 w-4" />
                       ) : message.speaker === 'candidate' ? (
                         <User className="h-4 w-4" />
                       ) : (
                         <Bot className="h-4 w-4" />
                       )}
                     </div>

                     {/* Message Content */}
                     <div className="flex-1 min-w-0">
                       <div className="flex items-center gap-2 mb-1">
                         {highlightedMessages[message.id] && (
                           <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                             <Sparkles className="h-3 w-3 mr-1" />
                             AI Highlighted
                           </Badge>
                         )}
                        <span className="text-sm font-medium">
                          {message.speaker === 'screening-agent' ? 'Screening Agent' : message.speaker.charAt(0).toUpperCase() + message.speaker.slice(1)}
                        </span>
                        {message.isQuestion && (
                          <Badge variant="outline" className="text-xs">
                            Question
                          </Badge>
                        )}
                        {searchTerm && message.content.toLowerCase().includes(searchTerm.toLowerCase()) && (
                          <Badge variant="secondary" className="text-xs">
                            Match
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm leading-relaxed text-foreground">
                        {highlightedMessages[message.id] ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: SecuritySanitizer.sanitizeHtml(highlightedMessages[message.id])
                            }}
                          />
                        ) : searchTerm ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: SecuritySanitizer.sanitizeHtml(
                                SecuritySanitizer.sanitizeText(message.content).replace(
                                  new RegExp(`(${SecuritySanitizer.sanitizeText(searchTerm)})`, 'gi'),
                                  '<mark class="bg-yellow-200 dark:bg-yellow-800">$1</mark>'
                                )
                              )
                            }}
                          />
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>

                    {/* Copy Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyMessage(message.content, message.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {copiedId === message.id ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Separator between messages */}
                  {index < filteredMessages.length - 1 && (
                    <Separator className="my-2 opacity-30" />
                  )}
                </div>
              ))}

              {filteredMessages.length === 0 && searchTerm && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No matches found for "{searchTerm}"</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
};